import { LegalLinks } from "components";
import { useBrand } from "hooks/useBrand";
import { useSeo } from "hooks/useSeo";
import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Paths from "routes/Paths";

import type { ContactMethod } from "./contact.types";
import ContactMethodCard from "./ContactMethodCard";

import styles from "./ContactPage.module.css";

/** Strips the display spacing from a phone number so it can be dialled from a `tel:` link. */
const toTelHref = (phone: string): string => `tel:${phone.replace(/\s/g, "")}`;

/**
 * Public Contact page. Deliberately lists the email, phone and registered address as selectable
 * text rather than hiding them behind a form: payment gateways check that a real business is
 * reachable by all three before enabling international payments, and so do customers.
 */
const ContactPage = () => {
	const { t, ready } = useTranslation(["contactPage", "common", "seo"]);
	const { brand } = useBrand();
	// Namespaces load over HTTP; until this one lands `t` echoes the raw key, which would flash in
	// the browser tab. useSeo leaves the title alone for a null, so wait for it. The description
	// comes from `seo`, fetched at boot, so the meta tag is populated from first paint.
	useSeo({
		title: ready ? t("contactPage:documentTitle") : null,
		description: t("seo:contact.description", { brand }),
		canonicalPath: Paths.contact
	});

	// Registered entity behind Tree Tier, as it appears on invoices, and the routes it is reachable
	// on. Held in `common:company` so the Contact page and the legal pages quote the same details.
	const company = t("common:company.name");
	const email = t("common:company.email");
	const phone = t("common:company.phone");
	const mobile = t("common:company.mobile");

	const contactMethods: ContactMethod[] = [
		{
			detail: email,
			href: `mailto:${email}`,
			note: t("contactPage:methods.email.note"),
			title: t("contactPage:methods.email.title")
		},
		{
			detail: phone,
			href: toTelHref(phone),
			note: t("contactPage:methods.phone.note"),
			title: t("contactPage:methods.phone.title")
		},
		{
			detail: mobile,
			href: toTelHref(mobile),
			note: t("contactPage:methods.mobile.note"),
			title: t("contactPage:methods.mobile.title")
		},
		{
			detail: t("contactPage:methods.address.detail", { address: t("common:company.address"), company }),
			href: null,
			note: t("contactPage:methods.address.note"),
			title: t("contactPage:methods.address.title")
		}
	];

	return (
		<main className={styles.page}>
			<header className={styles.header}>
				<Link aria-label={t("common:brandHome", { brand })} className={styles.brand} to={Paths.root}>
					<img alt="" className={styles.brandMark} src="/assets/landing/main-logo.png" />
					<span>{brand}</span>
				</Link>
				<Link className={styles.homeLink} to={Paths.root}>
					{t("common:backToHome")}
				</Link>
			</header>

			<section className={styles.hero}>
				<div className={styles.heroInner}>
					<p className={styles.eyebrow}>{t("contactPage:eyebrow", { brand })}</p>
					<h1>{t("contactPage:title")}</h1>
					<div aria-hidden="true" className={styles.flourish} />
					<p className={styles.heroSubtitle}>{t("contactPage:subtitle", { brand, company })}</p>
				</div>
			</section>

			<div className={styles.body}>
				<div className={styles.methodGrid}>
					{contactMethods.map((method: ContactMethod) => (
						<ContactMethodCard
							key={method.title}
							detail={method.detail}
							href={method.href}
							note={method.note}
							title={method.title}
						/>
					))}
				</div>

				<section aria-labelledby="ticket-heading" className={styles.ticketCard}>
					<h2 className={styles.cardHeading} id="ticket-heading">
						{t("contactPage:ticket.heading")}
					</h2>
					<p className={styles.cardSubheading}>{t("contactPage:ticket.description")}</p>
					<Link className={styles.ticketLink} to={Paths.helpSupport}>
						{t("contactPage:ticket.cta")}
					</Link>
				</section>
			</div>

			<footer className={styles.footer}>
				<span>{t("common:copyright")}</span>
				<nav aria-label={t("common:sitePagesLabel")} className={styles.footerNav}>
					<LegalLinks />
				</nav>
			</footer>
		</main>
	);
};

export default ContactPage;
